import { MeshInstance } from "playcanvas";
import { CameraInteractionGate } from "./camera-interaction-gate";
import {
	CapturedDragGesture,
	DragPosition,
	DragFinishReason,
	GestureOwner,
} from "./captured-drag-gesture";
import { PickRequestContext, PickScheduler } from "./pick-scheduler";
import { ScenePicker } from "./scene-picker";

type InteractionEvent = Interact.DragEvent | Interact.PointerEvent<"move">;

/** A tool that accepted a drag start. */
type StartTool = { cancel(): void };

/** The scene that pointer gestures pick in and deliver to. */
export interface PointerGestureHost<Tool extends StartTool> {
	/** Delivers an event to its handlers, reporting a tool that accepted a drag start. */
	dispatch(
		type: string,
		x: number,
		y: number,
		picked?: MeshInstance,
	): { handled: boolean; startTool?: Tool };
	captureActiveDrag(): GestureOwner | undefined;
	/** Captures the owner of an accepted drag start, limited to the scene it was captured in; `invalidate` completes the gesture without writing, also while it delivers or replays. */
	capture(
		tool: Tool | undefined,
		invalidate: () => void,
	): GestureOwner | undefined;
	enableCameraInteractions(enabled: boolean): void;
}

type Gesture = {
	interaction: object;
	pointerId?: number;
	ended: boolean;
	started: boolean;
	/** Its start read has begun, so that read predates any later selection. */
	readingStart?: boolean;
	/** A legacy owner handled one of its events; until then movement reads coalesce. */
	owned?: boolean;
	captured?: CapturedDragGesture;
	pending: DragPosition[];
	replaying: boolean;
	terminal?: DragFinishReason;
	x: number;
	y: number;
	release(): void;
};

/** Orders pointer gestures behind their reads and delivers captured drags synchronously. */
export class PointerGestures<Tool extends StartTool> {
	private _pointerWindow?: Window;
	private _pressedPointer?: number;
	private _gesture?: Gesture;
	/** The tool that accepted the latest dispatched start; a dragstart whose read fails cancels it. */
	private _acceptedStartTool?: Tool;
	private _deferredTapEffects: { owner: CapturedDragGesture; apply(): void }[] =
		[];
	private _flushingTapEffects = false;
	private _cameraGate = new CameraInteractionGate((enabled) =>
		this._host.enableCameraInteractions(enabled),
	);
	constructor(
		private _canvas: HTMLCanvasElement,
		private _pickScheduler: PickScheduler,
		private _scenePicker: ScenePicker,
		private _host: PointerGestureHost<Tool>,
	) {
		_canvas.addEventListener("pointerdown", this._onPointerPressed);
		_canvas.addEventListener("pointerup", this._onPointerReleased);
		_canvas.addEventListener("pointercancel", this._onPointerCanceled);
		_canvas.addEventListener("lostpointercapture", this._onPointerCanceled);
		this._pointerWindow = _canvas.ownerDocument?.defaultView ?? undefined;
		this._pointerWindow?.addEventListener(
			"pointerup",
			this._onPointerReleased,
			true,
		);
		this._pointerWindow?.addEventListener(
			"pointercancel",
			this._onPointerCanceled,
			true,
		);
	}

	/** Handles an interaction the scene is ready for; a captured gesture receives it before this returns. */
	public handleInteraction(ev: InteractionEvent): void {
		const { type, clientX: x, clientY: y, interaction } = ev;
		this._scenePicker.ensureState();
		if (
			type === "move" &&
			(this._gesture?.captured?.isLive ||
				(this._gesture && !this._gesture.started))
		) {
			return;
		}
		if (type === "dragstart") {
			this._finishGesture();
			this._gesture = {
				interaction,
				pointerId: this._interactionPointerId(ev),
				ended: false,
				started: false,
				pending: [],
				replaying: false,
				x,
				y,
				release: this._cameraGate.acquire(),
			};
		}
		const gesture = type === "move" ? undefined : this._gesture;
		if (type !== "move" && (!gesture || gesture.interaction !== interaction)) {
			return;
		}
		if (gesture) {
			if (gesture.ended) {
				return;
			}
			gesture.x = x;
			gesture.y = y;
		}
		if (gesture?.captured) {
			try {
				if (type === "dragend") {
					this._finishGesture("release");
				} else if (type === "dragmove") {
					if (gesture.replaying) {
						gesture.pending.push({ x, y });
					} else {
						gesture.captured.move({ x, y });
					}
				}
			} catch (error) {
				console.error("Interaction delivery failed", error);
			}
			return;
		}
		if (type === "dragmove" && gesture && !gesture.started) {
			gesture.pending.push({ x, y });
		}
		if (type === "dragend" && gesture) {
			gesture.ended = true;
			if (!gesture.started) {
				this._cameraGate.setEnabled(true);
				gesture.release();
			}
		}
		const work = async (context: PickRequestContext) => {
			try {
				if (
					gesture &&
					((!gesture.started && gesture.ended) || gesture !== this._gesture)
				) {
					return;
				}
				// Pending movement keeps its original FIFO slot for legacy owners.
				if (gesture?.captured) {
					return;
				}
				if (type === "dragstart" && gesture) {
					gesture.readingStart = true;
				}
				const picked = (await this._scenePicker.read(context, x, y))?.[0];
				if (type === "dragstart") {
					this._flushDeferredTapEffects();
				}
				if (
					type === "move" &&
					(this._gesture?.captured?.isLive ||
						(this._gesture && !this._gesture.started))
				) {
					return;
				}
				if (
					!context.isCurrent() ||
					(gesture &&
						((!gesture.started && gesture.ended) || gesture !== this._gesture))
				) {
					return;
				}
				if (type === "dragstart" && gesture) {
					gesture.started = true;
				}
				this._acceptedStartTool = undefined;
				const { handled, startTool } = this._host.dispatch(type, x, y, picked);
				this._acceptedStartTool = startTool;
				if (handled && gesture) {
					gesture.owned = true;
				}
				if (
					type === "dragstart" &&
					gesture &&
					gesture === this._gesture &&
					!gesture.ended
				) {
					const captured = this._host.capture(this._acceptedStartTool, () =>
						this._finishGesture("invalidate", gesture),
					);
					if (captured) {
						gesture.captured = new CapturedDragGesture(
							captured,
							() => {
								if (this._gesture === gesture) {
									this._gesture = undefined;
								}
								gesture.ended = true;
								this._cameraGate.setEnabled(true);
								gesture.release();
								this._flushDeferredTapEffects();
							},
							{ x, y },
						);
						gesture.replaying = true;
						try {
							while (
								gesture.pending.length &&
								gesture === this._gesture &&
								gesture.captured.isLive
							) {
								gesture.captured.move(gesture.pending.shift()!);
							}
						} finally {
							gesture.replaying = false;
						}
						if (gesture.terminal) {
							this._finishGesture(gesture.terminal, gesture);
						}
						return;
					}
					gesture.pending.length = 0;
				}
				if (gesture && gesture !== this._gesture) {
					return;
				}
				this._cameraGate.setEnabled(type === "dragend" || !handled);
				if (type === "dragend" && gesture === this._gesture) {
					this._gesture = undefined;
				}
			} catch (error) {
				if (gesture === this._gesture) {
					if (type === "dragstart" && gesture && !gesture.captured) {
						const failedOwner = this._host.captureActiveDrag();
						if (failedOwner) {
							gesture.started = false;
							failedOwner.invalidate();
						}
						this._acceptedStartTool?.cancel();
					}
					this._finishGesture();
				}
				throw error;
			} finally {
				if (type === "dragstart" && !gesture?.captured) {
					gesture?.release();
				}
			}
		};
		// Unowned movement (camera orbit) only needs its latest read; legacy owners keep every sample.
		const pending =
			type === "move"
				? this._pickScheduler.scheduleHover(work)
				: type === "dragmove" && gesture?.started && !gesture.owned
					? this._pickScheduler.scheduleLatest(gesture, work)
					: this._pickScheduler.schedule(work);
		void pending.catch((error) =>
			console.error("Interaction readback failed", error),
		);
	}

	/** Applies a tap effect now, or once the live captured gesture it would overtake completes. */
	public applyTapEffect(apply: () => void): void {
		const owner = this._gesture?.captured;
		if (owner?.isLive) {
			this._deferredTapEffects.push({ owner, apply });
		} else {
			this._flushDeferredTapEffects();
			apply();
		}
	}
	/** Drops deferred tap effects, cancels the current gesture and re-enables the camera. */
	public invalidate(): void {
		this._deferredTapEffects.length = 0;
		try {
			this._finishGesture();
		} finally {
			this._cameraGate.reset();
		}
	}
	/** Cancels the current gesture. */
	public finish(): void {
		this._finishGesture();
	}
	/** Cancels a captured gesture, or one whose start read has begun. */
	public finishEarlierReads(): void {
		const gesture = this._gesture;
		if (gesture?.captured || (gesture?.readingStart && !gesture.started)) {
			this._finishGesture();
		}
	}
	public removeListeners(): void {
		this._canvas.removeEventListener("pointerdown", this._onPointerPressed);
		this._canvas.removeEventListener("pointerup", this._onPointerReleased);
		this._canvas.removeEventListener("pointercancel", this._onPointerCanceled);
		this._canvas.removeEventListener(
			"lostpointercapture",
			this._onPointerCanceled,
		);
		this._pointerWindow?.removeEventListener(
			"pointerup",
			this._onPointerReleased,
			true,
		);
		this._pointerWindow?.removeEventListener(
			"pointercancel",
			this._onPointerCanceled,
			true,
		);
	}

	private _flushDeferredTapEffects(): void {
		if (this._flushingTapEffects) {
			return;
		}
		this._flushingTapEffects = true;
		try {
			while (
				this._deferredTapEffects.length &&
				!this._deferredTapEffects[0].owner.isLive
			) {
				this._deferredTapEffects.shift()!.apply();
			}
		} finally {
			this._flushingTapEffects = false;
		}
	}
	private _interactionPointerId(ev: InteractionEvent): number | undefined {
		if ("originalEvent" in ev && "pointerId" in ev.originalEvent) {
			return Number(ev.originalEvent.pointerId);
		}
		return this._pressedPointer;
	}
	private _onPointerPressed = (ev: PointerEvent) => {
		if (ev.isPrimary && ev.button === 0) {
			this._pressedPointer = ev.pointerId;
		}
	};
	private _matchesPointer(ev: PointerEvent): boolean {
		return (
			!!this._gesture &&
			(this._gesture.pointerId === undefined ||
				this._gesture.pointerId === ev.pointerId)
		);
	}
	private _onPointerReleased = (ev: PointerEvent) => {
		if (this._pressedPointer === ev.pointerId) {
			this._pressedPointer = undefined;
		}
		const gesture = this._gesture;
		if (gesture && this._matchesPointer(ev)) {
			this._scenePicker.ensureState();
			if (this._gesture !== gesture) {
				return;
			}
			if (gesture.captured) {
				gesture.x = ev.clientX;
				gesture.y = ev.clientY;
				try {
					this._finishGesture("release");
				} catch (error) {
					console.error("Interaction delivery failed", error);
				}
				return;
			}
			if (!gesture.started) {
				gesture.ended = true;
				this._gesture = undefined;
				this._cameraGate.setEnabled(true);
				gesture.release();
			} else {
				gesture.x = ev.clientX;
				gesture.y = ev.clientY;
				// Let Interact queue its legacy end first; provide a fallback otherwise.
				queueMicrotask(() => {
					void this._pickScheduler
						.schedule(async (context) => {
							try {
								if (this._gesture === gesture) {
									const picked = (
										await this._scenePicker.read(context, gesture.x, gesture.y)
									)?.[0];
									if (context.isCurrent() && this._gesture === gesture) {
										this._finishGesture("release", gesture, picked);
									}
								}
							} catch (error) {
								if (this._gesture === gesture) {
									this._finishGesture("cancel", gesture);
								}
								throw error;
							}
						})
						.catch((error) =>
							console.error("Interaction delivery failed", error),
						);
				});
			}
		}
	};
	private _onPointerCanceled = (ev: PointerEvent) => {
		if (this._pressedPointer === ev.pointerId) {
			this._pressedPointer = undefined;
		}
		if (this._matchesPointer(ev)) {
			try {
				this._finishGesture();
			} catch (error) {
				console.error("Interaction delivery failed", error);
			}
		}
	};
	private _finishGesture(
		reason: DragFinishReason = "cancel",
		gesture = this._gesture,
		picked?: MeshInstance,
	): void {
		if (gesture?.captured && gesture.replaying) {
			gesture.ended = true;
			gesture.terminal = reason;
			if (reason !== "release") {
				gesture.pending.length = 0;
			}
			return;
		}
		if (this._gesture === gesture) {
			this._gesture = undefined;
		}
		if (gesture) {
			gesture.ended = true;
			if (gesture.captured) {
				gesture.captured.finish({ x: gesture.x, y: gesture.y }, reason);
				return;
			}
			try {
				if (gesture.started) {
					this._host.dispatch("dragend", gesture.x, gesture.y, picked);
				}
			} finally {
				this._cameraGate.setEnabled(true);
				gesture.release();
			}
		}
	}
}
